import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight, Star, ShoppingBag, Layers } from "lucide-react";
import TribeLogo, { TribeMark } from "@/components/TribeLogo";
import { API } from "@/lib/api";


const STEPS = ["business", "rewards"];

export default function VendorOnboardingPage() {
  const { authHeaders, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    industry: "",
    reward_logic: "spend_based",
    points_per_pound: 10,
    reward_threshold: 500,
    reward_value_gbp: 5,
    reward_expiry_days: 30,
    orders_per_reward: 10,
  });

  const handleFinish = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        points_per_pound: parseFloat(form.points_per_pound),
        reward_threshold: parseInt(form.reward_threshold),
        reward_value_gbp: parseFloat(form.reward_value_gbp),
        reward_expiry_days: parseInt(form.reward_expiry_days),
        orders_per_reward: parseInt(form.orders_per_reward),
        tier_config: [],
        item_categories: [],
      };
      await axios.post(`${API}/vendors/setup`, payload, { headers: authHeaders() });
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-lg" data-testid="vendor-onboarding">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <TribeMark size={40} className="rounded-lg" />
          <div>
            <TribeLogo size="sm" />
            <p className="text-xs text-quiet">Set Up Your Business</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? "bg-purple-500 text-white" : "bg-line text-ink/50"}`}>
                {i + 1}
              </div>
              <span className={`text-xs font-medium ${i <= step ? "text-ink" : "text-ink/40"}`}>
                {s === "business" ? "Business Info" : "Rewards Setup"}
              </span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-line" />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-ink/10 shadow-sm p-6">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-ink">Tell us about your business</h2>
                <p className="text-sm text-ink/50 mt-1">This information will appear to your customers.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Business Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. The Coffee House" data-testid="onboarding-name" className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Industry</Label>
                <Select value={form.industry || ""} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger data-testid="onboarding-industry" className="h-10"><SelectValue placeholder="Select your industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="food_beverage">Food & Beverage</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="fitness">Fitness & Wellness</SelectItem>
                    <SelectItem value="beauty">Beauty & Salon</SelectItem>
                    <SelectItem value="entertainment">Entertainment</SelectItem>
                    <SelectItem value="services">Professional Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setStep(1)} disabled={!form.name || !form.industry} data-testid="onboarding-next" className="w-full h-10 bg-purple-500 hover:bg-purple-600 text-white gap-2">
                Next <ArrowRight size={14} />
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-ink">Configure your reward system</h2>
                <p className="text-sm text-ink/50 mt-1">Choose how your customers earn rewards.</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest">Reward Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: "spend_based", label: "Points per Spend", icon: Star, desc: "Earn points for every pound spent" },
                    { val: "order_based", label: "Stamp Card", icon: ShoppingBag, desc: "Reward after X orders" },
                    { val: "tier_based", label: "Tiered Rewards", icon: Layers, desc: "Bronze, Silver, Gold tiers" },
                  ].map(({ val, label, icon: Icon, desc }) => (
                    <button key={val} type="button" onClick={() => setForm({ ...form, reward_logic: val })}
                      data-testid={`onboarding-logic-${val}`}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${form.reward_logic === val ? "border-purple-500 bg-purple-50" : "border-ink/10 hover:border-ink/15"}`}>
                      <Icon size={18} className={form.reward_logic === val ? "text-ink mb-1" : "text-ink/40 mb-1"} />
                      <p className="text-xs font-semibold text-ink">{label}</p>
                      <p className="text-[10px] text-ink/50 leading-tight mt-0.5">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {form.reward_logic === "spend_based" && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Points per £1</Label>
                    <Input type="number" min="1" value={form.points_per_pound} onChange={(e) => setForm({ ...form, points_per_pound: e.target.value })} data-testid="onboarding-ppp" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Threshold (pts)</Label>
                    <Input type="number" min="1" value={form.reward_threshold} onChange={(e) => setForm({ ...form, reward_threshold: e.target.value })} data-testid="onboarding-threshold" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Reward Value (£)</Label>
                    <Input type="number" min="0.01" step="0.01" value={form.reward_value_gbp} onChange={(e) => setForm({ ...form, reward_value_gbp: e.target.value })} data-testid="onboarding-reward" />
                  </div>
                </div>
              )}

              {form.reward_logic === "order_based" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Orders for Reward</Label>
                    <Input type="number" min="1" value={form.orders_per_reward} onChange={(e) => setForm({ ...form, orders_per_reward: e.target.value })} data-testid="onboarding-orders" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Reward Value (£)</Label>
                    <Input type="number" min="0.01" step="0.01" value={form.reward_value_gbp} onChange={(e) => setForm({ ...form, reward_value_gbp: e.target.value })} data-testid="onboarding-reward" />
                  </div>
                </div>
              )}

              {form.reward_logic === "tier_based" && (
                <div className="p-3 bg-purple-50 border border-warning/40 rounded-md">
                  <p className="text-xs text-ink">Default tiers (Bronze/Silver/Gold) will be created. You can customize them later in your settings.</p>
                  <div className="mt-2 space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest">Points per £1</Label>
                    <Input type="number" min="1" value={form.points_per_pound} onChange={(e) => setForm({ ...form, points_per_pound: e.target.value })} className="max-w-xs" data-testid="onboarding-ppp" />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Reward Expiry (days)</Label>
                <Input type="number" min="1" value={form.reward_expiry_days} onChange={(e) => setForm({ ...form, reward_expiry_days: e.target.value })} data-testid="onboarding-expiry" className="max-w-xs" />
              </div>

              {error && <div className="px-3 py-2 rounded-md bg-danger/10 border border-danger/30 text-danger text-sm">{error}</div>}

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(0)} className="flex-1 h-10">Back</Button>
                <Button onClick={handleFinish} disabled={saving} data-testid="onboarding-finish" className="flex-1 h-10 bg-purple-500 hover:bg-purple-600 text-white">
                  {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  {saving ? "Setting up..." : "Complete Setup"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
